import { useCallback, useState } from 'react'
import { MuiOtpInput, MuiOtpInputProps } from 'mui-one-time-password-input'
import { Box, CircularProgress, Link, Typography } from '@mui/material'
import { useCountdown } from '@/hooks'
import { useSendAliasVerification, useValidateAlias } from '@/api/user-manager'

interface VerificationCodeProps extends MuiOtpInputProps {
  type: 'email' | 'phone'
  data: string
  onSuccess?: () => void
}

const SECONDS_TO_RESEND = 60

export function VerificationCode({
  data,
  type,
  onSuccess,
  ...props
}: VerificationCodeProps) {
  const [verificationCode, setVerificationCode] = useState('')

  const {
    mutateAsync: sendVerificationAlias,
    isPending: sendingVerificationAlias,
  } = useSendAliasVerification()

  const { mutateAsync: validateAlias, isError: validateAliasError } =
    useValidateAlias({
      onSuccess: () => onSuccess?.(),
    })

  const { count, startCountdown } = useCountdown({
    countStart: SECONDS_TO_RESEND,
  })

  const handleChangeVerificationCode = (newValue: string) => {
    setVerificationCode(newValue)
  }

  const handleCompleteVerificationCode = (token: string) => {
    validateAlias({
      alias: data,
      alias_type: type,
      token_value: token,
    })
  }

  const sendVerificationCode = useCallback(() => {
    sendVerificationAlias({
        alias: data,
        alias_type: type,
      })
  }, [data, sendVerificationAlias, type])

  const handleResubmitCode = () => {
    sendVerificationCode()
    startCountdown()
  }

  if (sendingVerificationAlias) {
    return (
      <Box textAlign="center">
        <CircularProgress size={24} />
      </Box>
    )
  }

  const isInvalidCode = validateAliasError

  return (
    <Box textAlign="center">
      <Typography mb={2}>
        Enviamos um código para <b>{data}</b>
      </Typography>

      <MuiOtpInput
        length={type === 'email' ? 4 : 6}
        value={verificationCode}
        onChange={handleChangeVerificationCode}
        onComplete={handleCompleteVerificationCode}
        autoFocus
        {...props}
      />

      {isInvalidCode && (
        <Typography color="error" mt={1}>
          Código inválido
        </Typography>
      )}

      <Typography variant="body2" mt={2}>
        {count === SECONDS_TO_RESEND ? (
          <>
            Ainda não recebeu?{' '}
            <Link onClick={handleResubmitCode}>Reenviar</Link>
          </>
        ) : (
          `Aguarde ${count} para reenviar.`
        )}
      </Typography>
    </Box>
  )
}
